import { jsPDF } from "jspdf";

const formatCurrency = (value) => `N${Number(value || 0).toLocaleString()}`;
const DEFAULT_HEADER_COLOR = [15, 77, 146];

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function hexToRgb(hexValue) {
  if (!hexValue) return null;
  const hex = String(hexValue).trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;

  if (hex.length === 3) {
    return [
      parseInt(hex[0] + hex[0], 16),
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16),
    ];
  }

  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function rgbStringToRgb(rgbValue) {
  const match = String(rgbValue || "")
    .trim()
    .match(/^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\)$/i);
  if (!match) return null;
  return [
    clampChannel(Number(match[1])),
    clampChannel(Number(match[2])),
    clampChannel(Number(match[3])),
  ];
}

function normalizeSchoolColor(rawColor) {
  if (!rawColor) return null;

  const candidate =
    typeof rawColor === "object"
      ? rawColor.value || rawColor.hex || rawColor.main || rawColor.color
      : rawColor;

  if (!candidate) return null;

  const value = String(candidate).trim().toLowerCase();
  if (value === "blue") return [37, 116, 209];

  if (value.startsWith("#")) {
    return hexToRgb(value);
  }

  if (value.startsWith("rgb")) {
    return rgbStringToRgb(value);
  }

  return null;
}

function getContrastingTextColor([r, g, b]) {
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? [17, 24, 39] : [255, 255, 255];
}

function tintColor([r, g, b], amount = 0.85) {
  return [
    clampChannel(r + (255 - r) * amount),
    clampChannel(g + (255 - g) * amount),
    clampChannel(b + (255 - b) * amount),
  ];
}

function buildStudentName(student) {
  return `${student?.first_name || ""} ${student?.last_name || ""}`.trim() || "-";
}

function buildReceiptData(invoice, school) {
  const totalAmount =
    invoice?.total_amount ??
    invoice?.items?.reduce((sum, item) => sum + (item.amount || 0), 0) ??
    0;
  const discountAmount =
    invoice?.discount_amount ?? invoice?.invoice_discount?.applied_amount ?? 0;
  const amountPaid = Number(invoice?.amount_paid || 0);
  const amountDue =
    invoice?.amount_due ?? Math.max(0, totalAmount - discountAmount - amountPaid);
  const isPaid = Boolean(invoice?.paid);
  const isPartial = Boolean(invoice?.partial_paid && !invoice?.paid);

  return {
    schoolName: school?.name || "School Receipt",
    schoolAddress: school?.address || school?.location || "",
    schoolPhone: school?.phone || "",
    schoolEmail: school?.email || "",
    receiptNo: `RCPT-${invoice?.id || "-"}`,
    invoiceNo: `INV-${invoice?.id || "-"}`,
    issuedOn: new Date().toLocaleString("en-GB"),
    paymentDate: invoice?.updated_at
      ? new Date(invoice.updated_at).toLocaleString("en-GB")
      : new Date().toLocaleString("en-GB"),
    statusLabel: isPaid ? "PAID" : isPartial ? "PARTIALLY PAID" : "UNPAID",
    studentName: buildStudentName(invoice?.student),
    className: invoice?.classroom?.name || "-",
    term: invoice?.term?.name || "-",
    session: invoice?.session?.name || "-",
    totalAmount,
    discountAmount,
    amountPaid,
    amountDue,
    items: invoice?.items || [],
  };
}

function drawTextPair(doc, label, value, x, y, maxWidth = 78) {
  doc.setFont("helvetica", "bold");
  doc.text(label, x, y);
  doc.setFont("helvetica", "normal");
  doc.text(String(value || "-"), x + 26, y, { maxWidth });
}

export function downloadInvoiceReceiptPdf(invoice, school) {
  const data = buildReceiptData(invoice, school);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const headerColor = normalizeSchoolColor(school?.color) || DEFAULT_HEADER_COLOR;
  const headerTextColor = getContrastingTextColor(headerColor);
  const tableHeaderBg = tintColor(headerColor, 0.88);

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 14;
  let y = margin;

  doc.setFillColor(...headerColor);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 26, 2, 2, "F");
  doc.setTextColor(...headerTextColor);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(data.schoolName, margin + 4, y + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const schoolMeta = [data.schoolAddress, data.schoolPhone, data.schoolEmail]
    .filter(Boolean)
    .join("  |  ");
  if (schoolMeta) {
    doc.text(schoolMeta, margin + 4, y + 16);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("PAYMENT RECEIPT", pageWidth - margin - 4, y + 10, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text(`Receipt No: ${data.receiptNo}`, pageWidth - margin - 4, y + 16, {
    align: "right",
  });

  y += 34;
  doc.setTextColor(25, 25, 25);
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 36, 2, 2, "FD");

  drawTextPair(doc, "Issued:", data.issuedOn, margin + 4, y + 8);
  drawTextPair(doc, "Payment Date:", data.paymentDate, margin + 4, y + 15);
  drawTextPair(doc, "Invoice No:", data.invoiceNo, margin + 4, y + 22);
  drawTextPair(doc, "Status:", data.statusLabel, margin + 4, y + 29);

  drawTextPair(doc, "Student:", data.studentName, margin + 98, y + 8, 80);
  drawTextPair(doc, "Class:", data.className, margin + 98, y + 15, 80);
  drawTextPair(doc, "Term:", data.term, margin + 98, y + 22, 80);
  drawTextPair(doc, "Session:", data.session, margin + 98, y + 29, 80);

  y += 44;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Fee Items", margin, y);
  y += 4;

  const tableWidth = pageWidth - margin * 2;
  const colName = 110;
  const colType = 34;

  doc.setFillColor(...tableHeaderBg);
  doc.rect(margin, y, tableWidth, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text("Item", margin + 2, y + 5.3);
  doc.text("Type", margin + colName + 2, y + 5.3);
  doc.text("Amount", margin + tableWidth - 2, y + 5.3, { align: "right" });
  y += 8;

  doc.setFont("helvetica", "normal");
  data.items.forEach((item, idx) => {
    const rowHeight = 7;
    if (y + rowHeight > pageHeight - 60) {
      doc.addPage();
      y = margin;
    }
    if (idx % 2 === 0) {
      doc.setFillColor(249, 250, 251);
      doc.rect(margin, y, tableWidth, rowHeight, "F");
    }
    doc.text(String(item?.name || "-"), margin + 2, y + 4.6, {
      maxWidth: colName - 4,
    });
    doc.text(item?.is_compulsory ? "Compulsory" : "Optional", margin + colName + 2, y + 4.6);
    doc.text(formatCurrency(item?.amount), margin + tableWidth - 2, y + 4.6, {
      align: "right",
    });
    y += rowHeight;
  });

  y += 8;
  const summaryBoxWidth = 74;
  const summaryX = pageWidth - margin - summaryBoxWidth;
  const summaryRows = [
    ["Subtotal", formatCurrency(data.totalAmount)],
    ["Discount", `- ${formatCurrency(data.discountAmount)}`],
    ["Amount Paid", formatCurrency(data.amountPaid)],
    ["Balance", formatCurrency(data.amountDue)],
  ];

  doc.setDrawColor(220, 224, 230);
  doc.roundedRect(summaryX, y, summaryBoxWidth, 34, 2, 2, "S");
  let rowY = y + 6;
  summaryRows.forEach(([label, value], index) => {
    doc.setFont("helvetica", index === summaryRows.length - 1 ? "bold" : "normal");
    doc.setFontSize(index === summaryRows.length - 1 ? 10.5 : 9.5);
    doc.text(label, summaryX + 3, rowY);
    doc.text(value, summaryX + summaryBoxWidth - 3, rowY, { align: "right" });
    rowY += 7;
  });

  const footerY = pageHeight - 22;
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.8);
  doc.setTextColor(70, 70, 70);
  doc.text(
    "Powered by Schoola",
    margin,
    footerY
  );
  doc.text(`Generated on ${data.issuedOn}`, pageWidth - margin, footerY, {
    align: "right",
  });

  const safeName = data.studentName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
  doc.save(`receipt_${safeName}_${invoice?.id || "invoice"}.pdf`);
}
